/**
 * What-if simulator — two rows per product, one for each commitment model we
 * offer: month-to-month (billed monthly) and annual commitment (billed
 * annually). Each row is a self-consistent per-seat economic model: profit =
 * (sell − buy) × seats, normalized to monthly recurring (annual ÷12). Buy/sell
 * are the seat-weighted effective prices across the customers on that model, so
 * the displayed numbers always multiply out to the shown profit. Overriding a
 * buy or sell price recomputes the projection live; overrides are ephemeral.
 *
 * Note: this is a modelling view. The per-customer-exact figures (which handle
 * seat mismatches between committed and billed seats separately) live in the
 * Profit by Product / Customer tabs.
 */

import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
} from '@mui/material';
import type { Cadence, ProfitBaselineRow } from '@techgitter/contracts';
import { modelOf, fmtMoney, fmtPct, type OverrideMap, type SkuOverride } from '../../utils/profitCompute';
import { headCellSx, bodyCellSx } from './profitFlags';

export interface ModelRow {
  sku: string;
  productName: string | null;
  model: Cadence;
  firstOfProduct: boolean;
  seats: number;
  buyNow: number | null;
  rrp: number | null;
  sellNow: number | null;
  profitNow: number | null;
  profitProjected: number | null;
  sellMrrNow: number;
  sellMrrProjected: number;
  buyField: keyof SkuOverride;
  sellField: keyof SkuOverride;
}

const MODEL_LABEL: Record<Cadence, string> = { monthly: 'Month-to-month', annual: 'Annual commitment' };

const seatsOf = (r: ProfitBaselineRow): number => r.seats || r.billedSeats;

/** Seat-weighted average of a per-seat price across rows (null if none known). */
function weightedPrice(rows: ProfitBaselineRow[], pick: (r: ProfitBaselineRow) => number | null): number | null {
  let sum = 0;
  let weight = 0;
  for (const r of rows) {
    const v = pick(r);
    const w = seatsOf(r);
    if (v !== null && w > 0) {
      sum += v * w;
      weight += w;
    }
  }
  return weight ? sum / weight : null;
}

const round2 = (v: number | null): number | null => (v === null ? null : Math.round(v * 100) / 100);

export function buildModelRows(rows: ProfitBaselineRow[], overrides: OverrideMap): ModelRow[] {
  const map = new Map<string, ProfitBaselineRow[]>();
  for (const r of rows) {
    if (!r.sku) continue;
    const list = map.get(r.sku);
    if (list) list.push(r);
    else map.set(r.sku, [r]);
  }

  const out: ModelRow[] = [];
  const skus = Array.from(map.keys()).sort((a, b) => {
    const an = map.get(a)![0].productName ?? a;
    const bn = map.get(b)![0].productName ?? b;
    return an.localeCompare(bn);
  });

  for (const sku of skus) {
    const group = map.get(sku)!;
    const productName = group[0].productName;
    const catalogueBuy: Record<Cadence, number | null> = {
      monthly: group[0].catalogueBuyM2mExTax,
      annual: group[0].catalogueBuyAnnualExTax,
    };
    const catalogueRrp: Record<Cadence, number | null> = {
      monthly: group[0].catalogueRrpM2mExTax,
      annual: group[0].catalogueRrpAnnualExTax,
    };
    let first = true;
    for (const model of ['monthly', 'annual'] as Cadence[]) {
      const modelRows = group.filter(r => modelOf(r) === model);
      // Skip a model only when there's neither a catalogue price nor customers.
      if (catalogueBuy[model] === null && modelRows.length === 0) continue;

      const override = overrides[sku];
      const seats = modelRows.reduce((n, r) => n + seatsOf(r), 0);
      // Annual prices are per-year; normalize the resulting profit to monthly.
      const cadenceDiv = model === 'annual' ? 12 : 1;

      const buyNow = weightedPrice(modelRows, r => r.buyUnitPriceExTax) ?? catalogueBuy[model];
      const sellNow = weightedPrice(modelRows, r => r.sellUnitPriceExTax);

      const buyField: keyof SkuOverride = model === 'annual' ? 'buyAnnualExTax' : 'buyM2mExTax';
      const sellField: keyof SkuOverride = model === 'annual' ? 'sellAnnualExTax' : 'sellM2mExTax';
      const buyOv = override?.[buyField];
      const sellOv = override?.[sellField];
      const buyEff = buyOv != null ? buyOv : buyNow;
      const sellEff = sellOv != null ? sellOv : sellNow;

      const profit = (buy: number | null, sell: number | null): number | null =>
        buy !== null && sell !== null ? ((sell - buy) * seats) / cadenceDiv : null;
      const sellMrr = (sell: number | null): number => (sell !== null ? (sell * seats) / cadenceDiv : 0);

      out.push({
        sku,
        productName,
        model,
        firstOfProduct: first,
        seats,
        buyNow: round2(buyNow),
        rrp: catalogueRrp[model],
        sellNow: round2(sellNow),
        profitNow: round2(profit(buyNow, sellNow)),
        profitProjected: round2(profit(buyEff, sellEff)),
        sellMrrNow: sellMrr(sellNow),
        sellMrrProjected: sellMrr(sellEff),
        buyField,
        sellField,
      });
      first = false;
    }
  }
  return out;
}

interface Totals {
  profit: number;
  sell: number;
  margin: number | null;
}

function totalsOf(rows: ModelRow[], projected: boolean): Totals {
  let profit = 0;
  let sell = 0;
  for (const r of rows) {
    const p = projected ? r.profitProjected : r.profitNow;
    if (p !== null) profit += p;
    sell += projected ? r.sellMrrProjected : r.sellMrrNow;
  }
  profit = Math.round(profit * 100) / 100;
  return { profit, sell, margin: sell ? profit / sell : null };
}

interface Props {
  rows: ProfitBaselineRow[];
  overrides: OverrideMap;
  /** Per-SKU % increase currently applied (for the input display). */
  pctBySku: Record<string, number>;
  onChange: (sku: string, override: SkuOverride) => void;
  onPctChange: (sku: string, pct: number | null) => void;
  onReset: () => void;
}

const numOrNull = (raw: string): number | null | undefined => {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isNaN(n) ? undefined : n;
};

export const WhatIfSimulatorPanel: React.FC<Props> = ({
  rows,
  overrides,
  pctBySku,
  onChange,
  onPctChange,
  onReset,
}) => {
  const modelRows = useMemo(() => buildModelRows(rows, overrides), [rows, overrides]);
  const baseline = useMemo(() => totalsOf(modelRows, false), [modelRows]);
  const projected = useMemo(() => totalsOf(modelRows, true), [modelRows]);
  const delta = projected.profit - baseline.profit;
  const hasOverrides = Object.keys(overrides).length > 0;

  const setField = (sku: string, field: keyof SkuOverride, raw: string) => {
    const value = numOrNull(raw);
    if (value === undefined) return; // not a number — ignore keystroke
    onChange(sku, { ...overrides[sku], [field]: value });
  };

  /** Apply a per-product % increase: bump every model's buy + sell by the %. */
  const applyPct = (sku: string, raw: string) => {
    if (raw.trim() === '') {
      onChange(sku, {}); // clears the SKU's overrides (parent drops empty)
      onPctChange(sku, null);
      return;
    }
    const pct = Number(raw);
    if (Number.isNaN(pct)) return;
    const factor = 1 + pct / 100;
    const ov: SkuOverride = {};
    for (const r of modelRows.filter(r => r.sku === sku)) {
      if (r.buyNow != null) ov[r.buyField] = Math.round(r.buyNow * factor * 100) / 100;
      if (r.sellNow != null) ov[r.sellField] = Math.round(r.sellNow * factor * 100) / 100;
    }
    onChange(sku, ov);
    onPctChange(sku, pct);
  };

  const priceInput = (sku: string, field: keyof SkuOverride, placeholder: number | null) => (
    <TextField
      size="small"
      type="number"
      variant="standard"
      placeholder={placeholder != null ? placeholder.toFixed(2) : '—'}
      value={overrides[sku]?.[field] ?? ''}
      onChange={e => setField(sku, field, e.target.value)}
      sx={{ width: 84 }}
      inputProps={{ style: { textAlign: 'right', fontSize: '0.8rem' }, min: 0, step: 0.01 }}
    />
  );

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={4} alignItems="center" flexWrap="wrap" useFlexGap>
          <Box>
            <Typography variant="caption" color="text.secondary">Baseline MRR profit</Typography>
            <Typography variant="h6">{fmtMoney(baseline.profit)}</Typography>
            <Typography variant="caption" color="text.secondary">{fmtPct(baseline.margin)} margin</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Projected MRR profit</Typography>
            <Typography variant="h6">{fmtMoney(projected.profit)}</Typography>
            <Typography variant="caption" color="text.secondary">{fmtPct(projected.margin)} margin</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Change</Typography>
            <Typography variant="h6" color={delta >= 0 ? 'success.main' : 'error.main'}>
              {delta >= 0 ? '+' : ''}{fmtMoney(delta)}/mo
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {(delta * 12 >= 0 ? '+' : '')}{fmtMoney(delta * 12)}/yr
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" onClick={onReset} disabled={!hasOverrides}>Reset overrides</Button>
        </Stack>
      </Paper>

      <Typography variant="body2" color="text.secondary">
        Each product has two rows — the two models we offer:{' '}
        <strong>month-to-month</strong> (no commitment) and <strong>annual commitment</strong>.
        Enter a per-product <strong>% increase</strong> to bump RRP, buy and sell together, or type
        a new buy/sell rate directly (overrides the %). All prices are per-seat GST-exclusive;
        profit is monthly recurring (MRR) = (sell − buy) × seats.
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Product</TableCell>
              <TableCell sx={headCellSx}>Model</TableCell>
              <TableCell sx={headCellSx} align="right">Seats</TableCell>
              <TableCell sx={headCellSx} align="right">% incr.</TableCell>
              <TableCell sx={headCellSx} align="right">RRP (now → new)</TableCell>
              <TableCell sx={headCellSx} align="right">Buy (now → new)</TableCell>
              <TableCell sx={headCellSx} align="right">Sell (now → new)</TableCell>
              <TableCell sx={headCellSx} align="right">Profit now</TableCell>
              <TableCell sx={headCellSx} align="right">Profit projected</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {modelRows.map((m, i) => {
              const changed = m.profitProjected !== m.profitNow;
              const pct = pctBySku[m.sku];
              const factor = pct != null ? 1 + pct / 100 : null;
              const rrpNew = m.rrp != null && factor != null ? m.rrp * factor : null;
              return (
                <TableRow
                  key={`${m.sku}::${m.model}`}
                  sx={{
                    backgroundColor: i % 2 === 1 ? 'action.hover' : 'transparent',
                    '& td': m.firstOfProduct ? { borderTop: '2px solid rgba(0,0,0,0.12)' } : undefined,
                  }}
                >
                  <TableCell sx={bodyCellSx}>{m.firstOfProduct ? (m.productName ?? m.sku) : ''}</TableCell>
                  <TableCell sx={bodyCellSx}>{MODEL_LABEL[m.model]}</TableCell>
                  <TableCell sx={bodyCellSx} align="right">{m.seats}</TableCell>
                  <TableCell sx={bodyCellSx} align="right">
                    {m.firstOfProduct && (
                      <TextField
                        size="small"
                        type="number"
                        variant="standard"
                        placeholder="0"
                        value={pctBySku[m.sku] ?? ''}
                        onChange={e => applyPct(m.sku, e.target.value)}
                        sx={{ width: 64 }}
                        inputProps={{ style: { textAlign: 'right', fontSize: '0.8rem' }, step: 0.5 }}
                        InputProps={{ endAdornment: <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>%</Box> }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, color: 'text.secondary' }} align="right">
                    {fmtMoney(m.rrp)}{rrpNew != null ? ` → ${fmtMoney(rrpNew)}` : ''}
                  </TableCell>
                  <TableCell sx={bodyCellSx} align="right">
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                      <span>{fmtMoney(m.buyNow)}</span>
                      <Box component="span" sx={{ color: 'text.disabled' }}>→</Box>
                      {priceInput(m.sku, m.buyField, m.buyNow)}
                    </Stack>
                  </TableCell>
                  <TableCell sx={bodyCellSx} align="right">
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                      <span>{fmtMoney(m.sellNow)}</span>
                      <Box component="span" sx={{ color: 'text.disabled' }}>→</Box>
                      {priceInput(m.sku, m.sellField, m.sellNow)}
                    </Stack>
                  </TableCell>
                  <TableCell sx={bodyCellSx} align="right">{fmtMoney(m.profitNow)}</TableCell>
                  <TableCell
                    sx={{ ...bodyCellSx, fontWeight: changed ? 700 : 400, color: changed ? ((m.profitProjected ?? 0) >= (m.profitNow ?? 0) ? 'success.main' : 'error.main') : undefined }}
                    align="right"
                  >
                    {fmtMoney(m.profitProjected)}
                  </TableCell>
                </TableRow>
              );
            })}
            {!modelRows.length && (
              <TableRow>
                <TableCell sx={bodyCellSx} colSpan={9} align="center">No priced SKUs to simulate.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};
